import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, memo, useMemo } from 'react';
import { Paperclip, Globe, Cpu, Plus, ArrowUp, Square, ShieldCheck, Zap, Brain, X, Loader2, AlertCircle, FileCode, FileText, FileJson, TableProperties, Maximize2, Activity, Wrench, FolderOpen, GitCompare, PackageX, KeyRound, Monitor, Unplug, CloudOff } from 'lucide-react';
import { PLC_BRANDS } from '../constants';
import { ChatSessionConfigSnapshot, GenerationConfig, type Message, PLC_LANGUAGES, PlcBrand, PlcLanguage, GenerationMode, UploadedFileSummary, languageForBrand, languagesForBrand, type PlcTargetProfile } from '../types';
import { CONVERSATION_FILE_LIMIT, DEFAULT_TIA_WORK_MODE, UPLOAD_FILE_ACCEPT, type TiaProjectRef, type TiaReadiness, type TiaText, type TiaWorkMode, type UploadEntry } from '@realplc/types';
import { getEffectiveLanguage, useTranslation } from '../i18n';
import { getSavedPlcBrand, getSavedPlcLanguage, getSavedPlcTargetProfile, getSavedTiaWorkMode, savePlcBrand, savePlcLanguage, savePlcTargetProfile, saveTiaWorkMode } from '../utils/plcPreferences';
import { TIA_WORK_MODE_TEXT } from '../utils/tiaSituations';
import { sessionTiaTargetStored, type TurnGenerationConfig } from '../utils/sessionTiaTarget';
import { composerSessionSettingsKey, type ComposerSessionSettings } from '../utils/composerSessionSettings';
import {
  buildProviderCredentialMaps,
  getCatalogModels,
  getCatalogProviders,
  getCustomModelConnections,
  getCustomModelId,
  getModelDisplayLabel,
  getProviderDisplayLabel,
  getSelectedProviderRuntimeConfig,
  isProviderSelectable,
  normalizeProvider,
  resolveModelSelection,
  saveCustomModelSelectionId,
} from '../utils/aiModelSettings';
import { loadProviderAvailability } from '../services/geminiService';
import { useChatCatalogStore } from '../stores/chatCatalogStore';

import {
  deleteUploadedFile,
  deriveUploadedFileCardStatus,
  describeRefusedUploads,
  fetchUploadedFilePreviewBlob,
  getUploadedFileParseStatus,
  isUploadedFileReadyForSend,
  refusedUploads,
  uploadChatFile,
  uploadedFileReadResult,
  uploadWarningMessages,
} from '../services/uploadedFiles';
import { getItem, setItem } from '../utils/scopedSettingsStorage';
import { clearComposerDraft, writeComposerDraft } from '../utils/composerDraft';
import { useComposerDraft } from '../hooks/useComposerDraft';
import {
  applyMention,
  collectMentionCandidates,
  filterMentions,
  filterSlashCommands,
  readMentionQuery,
  readSlashQuery,
  type MentionCandidate,
  type SlashCommand,
} from '../utils/composerMentions';
import { ComposerPicker } from './composer/ComposerPicker';
import { TiaProjectPicker } from './tia/TiaProjectPicker';
import { ConversationFiles } from './ConversationFiles';
import { createPortal } from 'react-dom';
import { ImagePreview } from './ImagePreview';
import { TiaAgentSettingsDialog } from './TiaAgentSettingsDialog';
import { CodesysAgentSettingsDialog } from './CodesysAgentSettingsDialog';
import { pickCodesysAgent, readCodesysCapabilityView } from './CodesysCapabilityCard';
import { useTiaAgentStatusStore } from '../stores/tiaAgentStatusStore';
import { useSessionTiaTargetStore } from '../stores/sessionTiaTargetStore';
import { readTiaReadiness, storeTiaProjectChoice } from '../services/opennessAgent';
import { OPEN_TIA_AGENT_SETTINGS_EVENT } from '../services/tiaAgentRelease';
import { OPEN_CODESYS_AGENT_SETTINGS_EVENT } from '../services/codesysAgent';
import { usePlanLimits } from '../hooks/usePlanLimits';
import { useConversationFiles } from '../hooks/useConversationFiles';
import { ContextUsageBar, ContextFullNotice } from './ChatLimitBar';

interface ChatInputProps {
  onSend: (prompt: string, config: GenerationConfig) => void;
  isLoading: boolean;
  onStop?: () => void;
  onNewConversation?: () => void;
  prefillRequest?: { id: number; text: string } | null;
  sessionConfig?: ChatSessionConfigSnapshot;
  isSuperAdmin?: boolean;
  hasActiveClarify?: boolean;
  activeClarifyState?: GenerationConfig['clarifyState'];
  sessionId?: string | null;
  projectId?: string | null;
  /** This session's messages, used to offer @ references to its artifacts. */
  conversationMessages?: Message[];
}

const ALLOWED_FILE_ACCEPT = UPLOAD_FILE_ACCEPT;
// The files of a conversation are a set, and a message adds to it.
const MAX_CHAT_FILES = CONVERSATION_FILE_LIMIT;
const UPLOAD_CONCURRENCY = 3;
const PARSE_STATUS_POLL_INTERVAL_MS = 1200;
const PARSE_STATUS_POLL_TIMEOUT_MS = 180000;

interface PreviewImage {
  imageUrl: string;
  fileName: string;
  revokeOnClose?: boolean;
}

function trimOptional(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

function formatFileSize(sizeBytes: number) {
  if (sizeBytes === 0) return '0 B';
  if (sizeBytes >= 1024 * 1024) {
    return `${(sizeBytes / 1024 / 1024).toFixed(1)} MB`;
  }
  return `${Math.max(1, Math.round(sizeBytes / 1024))} KB`;
}

function isAutomaticModelSelection(provider?: string, model?: string) {
  const normalizedProvider = (provider || '').trim().toLowerCase();
  const normalizedModel = (model || '').trim().toLowerCase();
  return normalizedProvider === 'autofree'
    || normalizedProvider === 'realplc'
    || normalizedModel === 'autofree';
}

/** Siemens calls it SCL, everyone else calls it ST */
function getStructuredTextAbbr(brand: PlcBrand): string {
  return brand === PlcBrand.Siemens ? 'SCL' : 'ST';
}

function getFileTypeIcon(fileName: string): React.ReactNode {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  const iconSize = 13;
  switch (ext) {
    case 'st':
    case 'scl':
    case 'l5x':
    case 'l5k':
    case 'xml':
      return <FileCode size={iconSize} className="shrink-0 text-cyan-500" />;
    case 'json':
      return <FileJson size={iconSize} className="shrink-0 text-yellow-500" />;
    case 'csv':
    case 'xlsx':
    case 'xls':
      return <TableProperties size={iconSize} className="shrink-0 text-green-500" />;
    case 'txt':
    case 'md':
    case 'log':
    case 'ini':
    case 'pdf':
    case 'docx':
    case 'pptx':
      return <FileText size={iconSize} className="shrink-0 text-blue-500" />;
    default:
      return <FileText size={iconSize} className="shrink-0 text-gray-500" />;
  }
}

async function runLimited<T>(
  tasks: Array<() => Promise<T>>,
  limit: number,
): Promise<PromiseSettledResult<T>[]> {
  const results: PromiseSettledResult<T>[] = new Array(tasks.length);
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < tasks.length) {
      const currentIndex = nextIndex++;
      try {
        results[currentIndex] = {
          status: 'fulfilled',
          value: await tasks[currentIndex](),
        };
      } catch (reason) {
        results[currentIndex] = {
          status: 'rejected',
          reason,
        };
      }
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(limit, tasks.length) }, () => worker()),
  );
  return results;
}

const ChatInputComponent: React.FC<ChatInputProps> = ({ onSend, isLoading, onStop, onNewConversation, prefillRequest, sessionConfig, isSuperAdmin, hasActiveClarify = false, activeClarifyState, sessionId, projectId, conversationMessages }) => {
  const { prompt, setPrompt, showRestoredDraftNotice, discardRestoredDraft } = useComposerDraft(sessionId);
  const { usage: contextUsage, maxChars, contextFull } = usePlanLimits(sessionId);

  const [brand, setBrand] = useState<PlcBrand>(() => sessionConfig?.brand ?? getSavedPlcTargetProfile().brand);
  const [language, setLanguage] = useState<PlcLanguage>(() => sessionConfig?.language ?? getSavedPlcLanguage());
  const [showSettings, setShowSettings] = useState(false);
  const [showPlusMenu, setShowPlusMenu] = useState(false);
  const [showModelMenu, setShowModelMenu] = useState(false);
  const [useWebSearch, setUseWebSearch] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileSummary[]>([]);
  const [uploadError, setUploadError] = useState('');
  const [uploadingCount, setUploadingCount] = useState(0);
  // What the conversation holds already. Read again when a turn is sent and
  // when it ends: a turn can add to it.
  const conversationFiles = useConversationFiles(sessionId, isLoading);
  const heldFileCount = conversationFiles.files.length;
  const [previewImage, setPreviewImage] = useState<PreviewImage | null>(null);
  const localPreviewUrlsRef = useRef<Map<string, string>>(new Map());
  const ownedPreviewUrlRef = useRef<string | null>(null);
  const parsePollControllersRef = useRef<Map<string, AbortController>>(new Map());
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);
  const [strictSafetyCheck, setStrictSafetyCheck] = useState(true);
  const [isScrollable, setIsScrollable] = useState(false);
  const [isExpandOpen, setIsExpandOpen] = useState(false);
  const [routingMode, setRoutingMode] = useState<GenerationMode>(() => {
    return (getItem('routing_mode') as GenerationMode) || GenerationMode.FAST;
  });
  const [selectedProvider, setSelectedProvider] = useState<string>('realplc');
  const [selectedModel, setSelectedModel] = useState<string>('autofree');
  const [settingsTick, setSettingsTick] = useState(0);
  const [showTiaAgentSettings, setShowTiaAgentSettings] = useState(false);
  const [showCodesysAgentSettings, setShowCodesysAgentSettings] = useState(false);
  const tiaAgentStatus = useTiaAgentStatusStore((state) => state.status);
  const sessionTiaProjectRef = useSessionTiaTargetStore((state) => state.tiaProjectRef);
  const tiaAgentLoadState = useTiaAgentStatusStore((state) => state.loadState);
  const refreshTiaAgentStatus = useTiaAgentStatusStore((state) => state.refresh);
  const startTiaAgentStatus = useTiaAgentStatusStore((state) => state.start);
  const [softwareVersion, setSoftwareVersion] = useState(() => getSavedPlcTargetProfile().softwareVersion || '');
  const [hardwareSeries, setHardwareSeries] = useState(() => getSavedPlcTargetProfile().hardwareSeries || '');
  const [plcModel, setPlcModel] = useState(() => getSavedPlcTargetProfile().plcModel || '');
  const [firmwareVersion, setFirmwareVersion] = useState(() => getSavedPlcTargetProfile().firmwareVersion || '');
  // How the Agent validates and creates TIA projects (P2 E12). Every turn reads
  // the saved value itself (AppSecure), so this only shows it.
  const [tiaWorkMode, setTiaWorkMode] = useState<TiaWorkMode>(() => getSavedTiaWorkMode());

  // `language` further down is the PLC output language, not the UI locale.
  const { t, language: uiLanguage } = useTranslation();

  // Apply visibility to the shared catalog for the current user's role.
  const catalog = useChatCatalogStore((s) => s.catalog);
  const catalogLoadState = useChatCatalogStore((s) => s.loadState);
  const effectiveProviders = useMemo(() => {
    const providers = getCatalogProviders({ isSuperAdmin });
    const hasCustomModel = !!getCustomModelId();
    const filtered = hasCustomModel ? providers : providers.filter((p) => p.value !== 'custom');
    const customIdx = filtered.findIndex((p) => p.value === 'custom');
    if (customIdx === -1) return filtered;
    return [...filtered.slice(0, customIdx), ...filtered.slice(customIdx + 1), filtered[customIdx]];
  }, [isSuperAdmin, settingsTick, catalog]);
  const effectiveModels = useMemo(
    () => getCatalogModels({ isSuperAdmin }),
    [catalog, isSuperAdmin],
  );
  // Which providers the platform can run. null until the lookup answers, and it
  // stays null when the lookup fails — `isProviderSelectable` offers everything
  // in that case rather than hiding a provider over a failed status call.
  const [platformProviders, setPlatformProviders] = useState<Record<string, boolean> | null>(null);
  useEffect(() => {
    let cancelled = false;
    void useChatCatalogStore.getState().load();
    loadProviderAvailability().then((providers) => {
      if (!cancelled) setPlatformProviders(providers);
    });
    return () => { cancelled = true; };
  }, []);
  const usesAutoModelPool = useMemo(
    () => isAutomaticModelSelection(selectedProvider, selectedModel),
    [selectedProvider, selectedModel],
  );
  const fastModeTitle = usesAutoModelPool
    ? t('Fast uses the low-latency automatic model pool.')
    : t('Fast keeps the selected model and uses lower-latency pipeline settings.');
  const thinkingModeTitle = usesAutoModelPool
    ? t('Thinking uses the deep automatic model pool.')
    : t('Thinking keeps the selected model and uses deeper planning/validation settings.');

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const plusMenuRef = useRef<HTMLDivElement>(null);
  const modelMenuRef = useRef<HTMLDivElement>(null);
  const settingsRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!prefillRequest) return;
    setPrompt(prefillRequest.text);
    writeComposerDraft(sessionId, prefillRequest.text);
    window.requestAnimationFrame(() => {
      const node = textareaRef.current;
      if (!node) return;
      node.focus();
      node.setSelectionRange(prefillRequest.text.length, prefillRequest.text.length);
    });
  }, [prefillRequest?.id, sessionId]);

  // ── Composer autocomplete: @ artifact references and / commands ──
  const [caret, setCaret] = useState(0);
  const [pickerIndex, setPickerIndex] = useState(0);
  const [pickerDismissed, setPickerDismissed] = useState(false);

  const mentionQuery = pickerDismissed ? null : readMentionQuery(prompt.slice(0, caret));
  const slashQuery = pickerDismissed ? null : readSlashQuery(prompt);

  const mentionCandidates = useMemo(
    () => collectMentionCandidates(conversationMessages || []),
    [conversationMessages],
  );
  const mentionMatches = useMemo(
    () => (mentionQuery === null ? [] : filterMentions(mentionCandidates, mentionQuery)),
    [mentionCandidates, mentionQuery],
  );
  const commandMatches = useMemo(
    () => (slashQuery === null ? [] : filterSlashCommands(slashQuery)),
    [slashQuery],
  );

  const pickerMode: 'mention' | 'command' | null = mentionMatches.length > 0
    ? 'mention'
    : commandMatches.length > 0
      ? 'command'
      : null;

  useEffect(() => { setPickerIndex(0); }, [mentionQuery, slashQuery]);

  const focusCaret = useCallback((position: number) => {
    requestAnimationFrame(() => {
      const node = textareaRef.current;
      if (!node) return;
      node.focus();
      node.setSelectionRange(position, position);
      setCaret(position);
    });
  }, []);

  const applyMentionPick = useCallback((candidate?: MentionCandidate) => {
    if (!candidate) return;
    const next = applyMention(prompt, caret, candidate.token);
    setPrompt(next.text);
    focusCaret(next.caret);
  }, [caret, focusCaret, prompt]);

  const applyCommandPick = useCallback((command?: SlashCommand) => {
    if (!command) return;
    setPrompt(command.expansion);
    focusCaret(command.expansion.length);
  }, [focusCaret]);

  const buildTargetProfile = useCallback((overrides: Partial<PlcTargetProfile> = {}): PlcTargetProfile => ({
    brand,
    softwareVersion: trimOptional(softwareVersion),
    hardwareSeries: trimOptional(hardwareSeries),
    plcModel: trimOptional(plcModel),
    firmwareVersion: trimOptional(firmwareVersion),
    ...overrides,
  }), [brand, softwareVersion, hardwareSeries, plcModel, firmwareVersion]);

  const updateTargetProfile = useCallback((overrides: Partial<PlcTargetProfile>) => {
    const next = buildTargetProfile(overrides);
    savePlcTargetProfile(next);
  }, [buildTargetProfile]);

  // Saved at once, like the rest of the panel, and read back: the saved value
  // has no subscription to follow.
  const chooseTiaWorkMode = useCallback((mode: TiaWorkMode) => {
    saveTiaWorkMode(mode);
    setTiaWorkMode(getSavedTiaWorkMode());
  }, []);

  const resetEngineeringContext = useCallback(() => {
    const defaultProfile: PlcTargetProfile = { brand: PlcBrand.Codesys };
    setBrand(PlcBrand.Codesys);
    setLanguage(PlcLanguage.ST);
    setSoftwareVersion('');
    setHardwareSeries('');
    setPlcModel('');
    setFirmwareVersion('');
    savePlcBrand(PlcBrand.Codesys);
    savePlcLanguage(PlcLanguage.ST);
    savePlcTargetProfile(defaultProfile);
    chooseTiaWorkMode(DEFAULT_TIA_WORK_MODE);
  }, [chooseTiaWorkMode]);

  // The picker's resume seam (P2 T11): a choice in a stored conversation goes
  // to the gateway at once, which resumes that conversation's validations
  // parked on it, and the page takes in the server's whole account at its
  // revision. A new conversation has nothing stored to write into: its first
  // turn carries the choice. A rejection leaves the choice dirty.
  const handleTiaProjectChosen = useCallback(async (chosenFor: string | null, ref: TiaProjectRef | null) => {
    if (!chosenFor || !projectId) return;
    const stored = await storeTiaProjectChoice(projectId, chosenFor, ref);
    sessionTiaTargetStored(chosenFor, stored.sessionTiaTarget, { revision: stored.sessionConfigRevision, whole: true });
  }, [projectId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (plusMenuRef.current && !plusMenuRef.current.contains(event.target as Node)) {
        setShowPlusMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modelMenuRef.current && !modelMenuRef.current.contains(event.target as Node)) {
        setShowModelMenu(false);
      }
      // Auto-collapse settings panel when clicking outside
      if (showSettings && settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setShowSettings(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSettings]);

  useEffect(() => () => {
    parsePollControllersRef.current.forEach(controller => controller.abort());
    parsePollControllersRef.current.clear();
    localPreviewUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    localPreviewUrlsRef.current.clear();
    if (ownedPreviewUrlRef.current) {
      URL.revokeObjectURL(ownedPreviewUrlRef.current);
      ownedPreviewUrlRef.current = null;
    }
  }, []);

  // Load the saved model on mount, and again once the catalog has answered.
  // Against the built-in list a provider added in the admin console resolves
  // to AutoFree, and writing that back would erase the user's choice on every
  // reload — so storage is only corrected after the catalog has settled.
  const catalogSettled = catalogLoadState === 'ready' || catalogLoadState === 'failed';
  useEffect(() => {
    const savedProvider = getItem('ai_provider');
    const savedModel = getItem('ai_model') || getItem('thinking_ai_model');
    const resolved = resolveModelSelection(savedProvider, savedModel, { isSuperAdmin });

    setSelectedProvider(resolved.provider);
    setSelectedModel(resolved.model);

    if (catalog && savedModel && resolved.model !== savedModel) {
      setItem('ai_provider', resolved.provider);
      setItem('ai_model', resolved.model);
    }

    if (getItem('ai_provider') === 'qwen') {
      setItem('ai_provider', 'aliyun');
    }
    if (getItem('ai_provider') === 'shengong') {
      setItem('ai_provider', 'realplc');
    }
  }, [catalogSettled, catalog, isSuperAdmin]);

  // Listen for settings updates
  useEffect(() => {
    const handleSettingsUpdate = () => {
      const savedProvider = getItem('ai_provider');
      const savedModel = getItem('ai_model');
      const resolved = resolveModelSelection(savedProvider, savedModel, { isSuperAdmin });
      setSelectedProvider(resolved.provider);
      setSelectedModel(resolved.model);
      setSettingsTick((t) => t + 1);
    };

    window.addEventListener('settings_updated', handleSettingsUpdate);
    return () => window.removeEventListener('settings_updated', handleSettingsUpdate);
  }, [isSuperAdmin]);

  // Restored when the conversation changes, or its own settings do. The server
  // writes the resolved CPU and the TIA project into the same snapshot while a
  // turn runs (P2 T2b); keyed on the snapshot object, this put back whatever
  // the user had edited here since the turn began. The key is the settings,
  // serialized, so the effect reads them from it.
  const restoredSettingsKey = composerSessionSettingsKey(sessionConfig);
  useLayoutEffect(() => {
    if (restoredSettingsKey) {
      const restored = JSON.parse(restoredSettingsKey) as ComposerSessionSettings;
      setBrand(restored.brand);
      setLanguage(restored.language);
      setRoutingMode(restored.routingMode);
      setSoftwareVersion(restored.softwareVersion);
      setHardwareSeries(restored.hardwareSeries);
      // The user's setting, never the model the server resolved (P2 E9 ④).
      setPlcModel(restored.plcModel);
      setFirmwareVersion(restored.firmwareVersion);

      // Only override provider/model from sessionConfig when both fields are present.
      // Otherwise keep the current localStorage-based selection to avoid silently
      // resetting the user's choice to realplc/autofree on poll-driven re-renders.
      if (restored.preferredProvider) {
        const resolved = resolveModelSelection(restored.preferredProvider, restored.preferredModel, { isSuperAdmin });
        setSelectedProvider(resolved.provider);
        setSelectedModel(resolved.model);
      }
      return;
    }

    const savedTargetProfile = getSavedPlcTargetProfile();
    setBrand(savedTargetProfile.brand || getSavedPlcBrand());
    setLanguage(getSavedPlcLanguage());
    setSoftwareVersion(savedTargetProfile.softwareVersion || '');
    setHardwareSeries(savedTargetProfile.hardwareSeries || '');
    setPlcModel(savedTargetProfile.plcModel || '');
    setFirmwareVersion(savedTargetProfile.firmwareVersion || '');
    const resolved = resolveModelSelection(
      getItem('ai_provider'),
      getItem('ai_model') || getItem('thinking_ai_model'),
      { isSuperAdmin },
    );
    setSelectedProvider(resolved.provider);
    setSelectedModel(resolved.model);
    setRoutingMode((getItem('routing_mode') as GenerationMode) || GenerationMode.FAST);
  }, [sessionId, restoredSettingsKey, isSuperAdmin]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const maxHeight = Math.max(200, window.innerHeight * 0.5);
      textareaRef.current.style.height = `${Math.min(Math.max(textareaRef.current.scrollHeight, 40), maxHeight)}px`;
      setIsScrollable(textareaRef.current.scrollHeight > textareaRef.current.clientHeight);
    }
  }, [prompt]);

  const allAttachmentsReady = useMemo(
    () => uploadedFiles.every(isUploadedFileReadyForSend),
    [uploadedFiles],
  );
  // A pending clarification no longer disables the composer. handleSubmit
  // carries activeClarifyState forward with the written answer, which is what
  // that state was added for — it was unreachable while this flag closed the
  // box, and the panel's own "或直接在下方输入回复" promised something the
  // reader could not do.
  const sendDisabled = (!prompt.trim() && uploadedFiles.length === 0)
    || uploadingCount > 0
    || isLoading
    || !allAttachmentsReady;

  const pollUploadedFileStatus = useCallback((file: UploadedFileSummary) => {
    const fileId = file.fileId;
    if (!fileId) return;

    parsePollControllersRef.current.get(fileId)?.abort();
    const controller = new AbortController();
    parsePollControllersRef.current.set(fileId, controller);

    const run = async () => {
      const startedAt = Date.now();
      while (!controller.signal.aborted && Date.now() - startedAt < PARSE_STATUS_POLL_TIMEOUT_MS) {
        try {
          const status = await getUploadedFileParseStatus(fileId);
          if (controller.signal.aborted) return;

          setUploadedFiles(current => current.map(item => {
            if ((item.fileId || item.localId) !== fileId) return item;
            return {
              ...item,
              uploadStatus: 'uploaded',
              scanStatus: status.scanStatus,
              parseStatus: status.parseStatus,
              conversionStatus: status.conversionStatus,
              contextStatus: status.contextStatus,
              contextKind: status.contextKind,
              contextRole: status.contextRole,
              sendableReason: status.sendableReason,
              cardStatus: status.cardStatus || deriveUploadedFileCardStatus(status),
              warnings: uploadWarningMessages(status.warnings),
            };
          }));

          const cardStatus = status.cardStatus || deriveUploadedFileCardStatus(status);
          if (cardStatus === 'ready' || cardStatus === 'warning' || cardStatus === 'failed') {
            return;
          }
        } catch (error) {
          if (!controller.signal.aborted) {
            setUploadError(error instanceof Error ? error.message : t('Failed to refresh file status'));
          }
          return;
        }

        await new Promise<void>((resolve) => {
          const timeoutId = window.setTimeout(resolve, PARSE_STATUS_POLL_INTERVAL_MS);
          controller.signal.addEventListener('abort', () => {
            window.clearTimeout(timeoutId);
            resolve();
          }, { once: true });
        });
      }

      if (!controller.signal.aborted) {
        const message = t('File processing timed out. Please retry upload.');
        setUploadedFiles(current => current.map(item => {
          if ((item.fileId || item.localId) !== fileId) return item;
          return {
            ...item,
            uploadStatus: 'uploaded',
            contextStatus: 'failed',
            cardStatus: 'failed',
            sendableReason: message,
            parseError: message,
          };
        }));
        setUploadError(message);
      }
    };

    void run().finally(() => {
      if (parsePollControllersRef.current.get(fileId) === controller) {
        parsePollControllersRef.current.delete(fileId);
      }
    });
  }, [t]);

  const closeImagePreview = useCallback(() => {
    if (ownedPreviewUrlRef.current) {
      URL.revokeObjectURL(ownedPreviewUrlRef.current);
      ownedPreviewUrlRef.current = null;
    }
    setPreviewImage(null);
  }, []);

  const openImagePreview = useCallback(async (file: UploadedFileSummary) => {
    const localUrl = localPreviewUrlsRef.current.get(file.fileId || file.localId || '');
    if (localUrl) {
      setPreviewImage({ imageUrl: localUrl, fileName: file.fileName });
      return;
    }
    if (!file.fileId) return;

    try {
      const blob = await fetchUploadedFilePreviewBlob(file.fileId);
      if (ownedPreviewUrlRef.current) {
        URL.revokeObjectURL(ownedPreviewUrlRef.current);
      }
      const imageUrl = URL.createObjectURL(blob);
      ownedPreviewUrlRef.current = imageUrl;
      setPreviewImage({ imageUrl, fileName: file.fileName, revokeOnClose: true });
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : t('Failed to load image preview'));
    }
  }, [t]);

  const handleSubmit = useCallback(() => {
    if ((!prompt.trim() && uploadedFiles.length === 0) || isLoading || uploadingCount > 0) return;
    if (!allAttachmentsReady) {
      // A file that failed will never finish: waiting is the wrong thing to ask for.
      setUploadError(uploadedFiles.some(file => file.cardStatus === 'failed')
        ? t('A file could not be read. Remove it to send.')
        : t('Please wait for file processing to finish.'));
      return;
    }

    const currentModel = selectedModel;
    const provider = selectedProvider;
    const { apiKey: savedApiKey, baseUrl: savedBaseUrl } = getSelectedProviderRuntimeConfig(provider);
    const { providerApiKeys, providerBaseUrls } = buildProviderCredentialMaps();
    const savedUiLanguage = getEffectiveLanguage();
    const savedCustomInstructions = getItem('custom_instructions') || '';
    const savedKnowledgeScope = getItem('knowledge_scope') || undefined;

    const promptText = prompt.trim() || t('Please analyze the uploaded files and provide suggestions within the RealPLC context.');

    const targetProfile = buildTargetProfile();

    const genConfig: TurnGenerationConfig = {
      brand,
      language: languageForBrand(brand, language),
      context: '',
      projectId: projectId || undefined,
      includeComments: true,
      addSafetyRoutines: false,
      strictSafetyCheck,
      customInstructions: savedCustomInstructions,
      model: currentModel,
      preferredModel: currentModel,
      preferredProvider: provider,
      useWebSearch,
      apiKey: savedApiKey,
      baseUrl: savedBaseUrl,
      providerApiKeys,
      providerBaseUrls,
      uiLanguage: savedUiLanguage,
      routingMode,
      knowledgeScope: savedKnowledgeScope,
      attachmentIds: uploadedFiles.map(file => file.fileId || file.localId),
      attachments: uploadedFiles,
      targetProfile,
      softwareVersion: targetProfile.softwareVersion,
      hardwareSeries: targetProfile.hardwareSeries,
      // The settings box, which is also `targetProfile.plcModel`.
      settingsPlcModel: targetProfile.plcModel,
      firmwareVersion: targetProfile.firmwareVersion,
    };

    // Carry active clarify context so follow-up text input (e.g. "继续")
    // preserves confirmed answers across token-quota or other interruptions.
    if (activeClarifyState?.confirmedAnswers?.length) {
      genConfig.isClarifyFollowUp = true;
      genConfig.clarifyState = activeClarifyState;
    }

    onSend(promptText, genConfig);
    setPrompt('');
    clearComposerDraft(sessionId);
    closeImagePreview();
    // Revoke all local blob URLs to prevent memory leaks
    localPreviewUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    localPreviewUrlsRef.current.clear();
    setUploadedFiles([]);
    setUploadError('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [prompt, uploadedFiles, uploadingCount, isLoading, allAttachmentsReady, selectedModel, selectedProvider, brand, language, useWebSearch, routingMode, strictSafetyCheck, buildTargetProfile, activeClarifyState, closeImagePreview, onSend, t]);

  const handleFilesSelected = useCallback(async (files: ArrayLike<File> | null) => {
    if (!files?.length) return;

    setUploadError('');
    const remainingSlots = Math.max(0, MAX_CHAT_FILES - heldFileCount - uploadedFiles.length);
    const selectedFiles = Array.from(files).slice(0, remainingSlots);
    if (selectedFiles.length === 0) {
      setUploadError(t('Maximum {n} files allowed').replace('{n}', String(MAX_CHAT_FILES)));
      return;
    }
    if (files.length > selectedFiles.length) {
      setUploadError(t('Maximum {n} files allowed').replace('{n}', String(MAX_CHAT_FILES)));
    }

    // Refused before anything is uploaded: an old Office file, a kind of file
    // no upload takes, a file over the conversation's size limit.
    const uploadEntry: UploadEntry = projectId ? 'project_chat' : 'new_chat';
    const refused = refusedUploads(selectedFiles, uploadEntry);
    if (refused.length > 0) {
      setUploadError(describeRefusedUploads(refused, uploadEntry, t));
      return;
    }

    // Create placeholder entries so progress bars are visible during upload
    const placeholderIds = selectedFiles.map(() =>
      `uploading_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`
    );
    // Generate local blob URLs for image previews during upload
    for (let i = 0; i < selectedFiles.length; i++) {
      if (selectedFiles[i].type.startsWith('image/')) {
        localPreviewUrlsRef.current.set(placeholderIds[i], URL.createObjectURL(selectedFiles[i]));
      }
    }
    setUploadingCount(count => count + selectedFiles.length);
    setUploadedFiles(current => [
      ...current,
      ...selectedFiles.map((file, i) => ({
        localId: placeholderIds[i],
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        uploadStatus: 'uploading' as const,
        cardStatus: 'uploading' as const,
        uploadProgress: 0,
      })),
    ].slice(0, MAX_CHAT_FILES));

    try {
      const results = await runLimited(
        selectedFiles.map((file, i) => () =>
          uploadChatFile(file, {
            sessionId,
            projectId,
            entry: uploadEntry,
          }, (progress) => {
            setUploadedFiles(current =>
              current.map(f =>
                f.localId === placeholderIds[i] ? { ...f, uploadProgress: progress } : f
              ),
            );
          }),
        ),
        UPLOAD_CONCURRENCY,
      );
      const succeeded: Array<{ index: number; file: UploadedFileSummary }> = [];
      const failed: string[] = [];

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          succeeded.push({
            index,
            file: {
              ...result.value,
              cardStatus: result.value.cardStatus || deriveUploadedFileCardStatus(result.value),
            },
          });
        } else {
          const message = result.reason instanceof Error ? result.reason.message : 'Upload failed';
          // The server's own sentences have translations; anything else reads as it is.
          failed.push(`${selectedFiles[index]?.name || 'file'}: ${t(message)}`);
        }
      });

      // Remap blob URLs from placeholder IDs to server file IDs for image thumbnails
      const remappedPreviewIds = new Set<string>();
      for (const item of succeeded) {
        const blobUrl = localPreviewUrlsRef.current.get(placeholderIds[item.index]);
        if (blobUrl) {
          localPreviewUrlsRef.current.set(item.file.fileId || item.file.localId, blobUrl);
          remappedPreviewIds.add(placeholderIds[item.index]);
        }
      }
      for (const placeholderId of placeholderIds) {
        const blobUrl = localPreviewUrlsRef.current.get(placeholderId);
        if (blobUrl && !remappedPreviewIds.has(placeholderId)) {
          URL.revokeObjectURL(blobUrl);
        }
        localPreviewUrlsRef.current.delete(placeholderId);
      }

      // Replace placeholders with finalized entries
      setUploadedFiles(current => [
        ...current.filter(f => !placeholderIds.includes(f.localId)),
        ...succeeded.map(item => item.file),
      ].slice(0, MAX_CHAT_FILES));

      succeeded.forEach(item => {
        if (!isUploadedFileReadyForSend(item.file)) {
          pollUploadedFileStatus(item.file);
        }
      });

      if (failed.length > 0) {
        setUploadError(failed.join('\n'));
      }
    } finally {
      setUploadingCount(count => Math.max(0, count - selectedFiles.length));
    }
  }, [sessionId, projectId, uploadedFiles.length, heldFileCount, pollUploadedFileStatus, t]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      void handleFilesSelected(e.dataTransfer.files);
    }
  }, [handleFilesSelected]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const combinedFiles: File[] = [];

    // 1. Collect file items from clipboard (e.g., files copied from Explorer)
    if (e.clipboardData?.files) {
      for (let i = 0; i < e.clipboardData.files.length; i++) {
        combinedFiles.push(e.clipboardData.files[i]);
      }
    }

    // 2. Collect raw image items (screenshots from Snipping Tool, PrintScreen, etc.)
    //    that aren't already included as clipboard files.
    const items = e.clipboardData?.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image/')) {
          const blob = item.getAsFile();
          if (blob) {
            // Avoid duplicates with files already collected from clipboardData.files
            const isDuplicate = combinedFiles.some(f =>
              f.size === blob.size && f.type === item.type
            );
            if (!isDuplicate) {
              const ext = item.type.split('/')[1] || 'png';
              const now = new Date();
              const ts = now.getFullYear()
                + String(now.getMonth() + 1).padStart(2, '0')
                + String(now.getDate()).padStart(2, '0') + '_'
                + String(now.getHours()).padStart(2, '0')
                + String(now.getMinutes()).padStart(2, '0')
                + String(now.getSeconds()).padStart(2, '0');
              const file = new File([blob], `Screenshot_${ts}.${ext}`, { type: item.type });
              combinedFiles.push(file);
            }
          }
        }
      }
    }

    if (combinedFiles.length > 0) {
      e.preventDefault();
      const dt = new DataTransfer();
      combinedFiles.forEach(f => dt.items.add(f));
      void handleFilesSelected(dt.files);
    }
  }, [handleFilesSelected]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // The picker owns the arrow keys and Enter while it is open, so choosing a
    // reference never sends a half-written prompt.
    if (pickerMode) {
      const count = pickerMode === 'mention' ? mentionMatches.length : commandMatches.length;
      if (count > 0 && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault();
        setPickerIndex(current => (e.key === 'ArrowDown'
          ? (current + 1) % count
          : (current - 1 + count) % count));
        return;
      }
      if (count > 0 && (e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey) {
        e.preventDefault();
        if (pickerMode === 'mention') applyMentionPick(mentionMatches[pickerIndex]);
        else applyCommandPick(commandMatches[pickerIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setPickerDismissed(true);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleExpandKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (sendDisabled) return;
      handleSubmit();
      setIsExpandOpen(false);
      textareaRef.current?.focus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsExpandOpen(false);
      textareaRef.current?.focus();
    }
  };

  const selectedBrandLabel = PLC_BRANDS.find((item) => item.value === brand)?.label.split(' (')[0] || brand;
  // `language` is what the user chose; a platform that does not offer it
  // (ladder on CODESYS) runs structured text, and keeps the choice for later.
  const effectiveLanguage = languageForBrand(brand, language);
  const selectedLanguageLabel = effectiveLanguage === PlcLanguage.ST
    ? getStructuredTextAbbr(brand)
    : PLC_LANGUAGES.find((item) => item.value === effectiveLanguage)?.abbreviation || effectiveLanguage;
  const tiaAgentEnabled = getItem('agent_tia_enabled') !== 'false';
  const showTiaAgentEntry = brand === PlcBrand.Siemens && tiaAgentEnabled;
  const codesysAgentEnabled = getItem('agent_codesys_enabled') !== 'false';
  const showCodesysAgentEntry = brand === PlcBrand.Codesys && codesysAgentEnabled;
  const connectionState = tiaAgentStatus?.connectionState ?? tiaAgentStatus?.status;
  const codesysAgent = pickCodesysAgent(tiaAgentStatus?.agents ?? [], projectId);
  const codesysCapability = readCodesysCapabilityView(
    codesysAgent?.capabilitySnapshot ?? codesysAgent?.capabilities,
  );
  const codesysReady = connectionState === 'online' && codesysCapability?.ready === true;
  // The readiness the TIA button, the Agent settings dialog and the
  // post-generation card all show (readTiaReadiness), in the readiness table's
  // words: 「在线 · 未选工程」, 「在线 · 工程就绪」. For the conversation's project.
  const tiaReadiness = useMemo(
    () => readTiaReadiness(tiaAgentStatus, projectId, sessionTiaProjectRef),
    [tiaAgentStatus, projectId, sessionTiaProjectRef],
  );
  const sayTia = (text: TiaText) => (uiLanguage === 'Chinese' ? text.zh : text.en);
  // No status has arrived yet: the first request is still out, so nothing is known.
  const tiaDetecting = !tiaAgentStatus && tiaAgentLoadState === 'detecting';
  const tiaStaleOffline = tiaAgentLoadState === 'stale' && tiaReadiness.state === 'offline';
  const tiaAgentButtonText = tiaDetecting ? t('Detecting') : sayTia(tiaReadiness.label);
  const tiaAgentButtonLabel = tiaDetecting
    ? t('Detecting TIA Agent')
    : tiaAgentLoadState === 'stale'
      ? `${t('TIA Agent status unavailable · showing last known state')}\n${sayTia(tiaReadiness.explanation)}`
      : sayTia(tiaReadiness.explanation);
  const statusIconClass = 'shrink-0';
  const tiaReadinessIcon = (state: TiaReadiness): React.ReactNode => {
    switch (state) {
      case 'ready':
        return <ShieldCheck size={14} className={`${statusIconClass} text-emerald-600 dark:text-emerald-400`} />;
      case 'busy':
        return <Activity size={14} className={`${statusIconClass} text-blue-600 dark:text-blue-400`} />;
      case 'checking':
        return <Loader2 size={14} className={`${statusIconClass} animate-spin text-zinc-500`} />;
      case 'offline':
        return <Unplug size={14} className={`${statusIconClass} text-red-600 dark:text-red-400`} />;
      case 'no_project_selected':
      case 'project_not_found':
      case 'upgrade_required':
        return <FolderOpen size={14} className={`${statusIconClass} text-amber-600 dark:text-amber-400`} />;
      case 'project_mismatch':
        return <GitCompare size={14} className={`${statusIconClass} text-orange-600 dark:text-orange-400`} />;
      case 'capability_missing':
        return <PackageX size={14} className={`${statusIconClass} text-purple-600 dark:text-purple-400`} />;
      case 'auth_required':
        return <KeyRound size={14} className={`${statusIconClass} text-red-600 dark:text-red-400`} />;
      case 'tia_not_running':
        return <Monitor size={14} className={`${statusIconClass} text-amber-600 dark:text-amber-400`} />;
      default:
        return <Wrench size={14} className={`${statusIconClass} text-amber-600 dark:text-amber-400`} />;
    }
  };
  const tiaAgentEntryIcon = tiaDetecting
    ? <Loader2 size={14} className={`${statusIconClass} animate-spin text-zinc-500`} />
    : tiaStaleOffline
      ? <CloudOff size={14} className={`${statusIconClass} text-zinc-500`} />
      : tiaReadiness.noProject
        // A conversation in no project: opening one is the way out, whatever the Agent's state.
        ? <FolderOpen size={14} className={`${statusIconClass} text-amber-600 dark:text-amber-400`} />
        : tiaReadinessIcon(tiaReadiness.state);
  // Green only where a validation can go now (busy queues it); amber for an
  // Agent that answers but cannot yet, red for none, grey while unknown.
  const tiaAgentEntryTone = tiaDetecting || tiaStaleOffline
    ? 'border-zinc-200 bg-zinc-50 text-zinc-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300'
    : tiaReadiness.state === 'offline'
      ? 'border-red-200 bg-red-50/70 text-red-700 hover:bg-red-100 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300'
      : tiaReadiness.canValidate
        ? 'border-emerald-200 bg-emerald-50/70 text-emerald-800 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300'
        : 'border-amber-200 bg-amber-50/70 text-amber-800 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300';
  const codesysAgentEntryIcon = connectionState !== 'online'
    ? tiaAgentLoadState === 'detecting'
      ? <Loader2 size={14} className={`${statusIconClass} animate-spin text-zinc-500`} />
      : tiaAgentLoadState === 'stale'
        ? <CloudOff size={14} className={`${statusIconClass} text-zinc-500`} />
        : <Unplug size={14} className={`${statusIconClass} text-red-600 dark:text-red-400`} />
    : codesysReady
      ? <ShieldCheck size={14} className={`${statusIconClass} text-emerald-600 dark:text-emerald-400`} />
      : <Wrench size={14} className={`${statusIconClass} text-amber-600 dark:text-amber-400`} />;
  const showAnyAgentEntry = showTiaAgentEntry || showCodesysAgentEntry;

  useEffect(() => {
    if (!showAnyAgentEntry) return;
    return startTiaAgentStatus();
  }, [showAnyAgentEntry, startTiaAgentStatus]);

  useEffect(() => {
    const openTiaAgentSettings = () => setShowTiaAgentSettings(true);
    const openCodesysAgentSettings = () => setShowCodesysAgentSettings(true);
    window.addEventListener(OPEN_TIA_AGENT_SETTINGS_EVENT, openTiaAgentSettings);
    window.addEventListener(OPEN_CODESYS_AGENT_SETTINGS_EVENT, openCodesysAgentSettings);
    return () => {
      window.removeEventListener(OPEN_TIA_AGENT_SETTINGS_EVENT, openTiaAgentSettings);
      window.removeEventListener(OPEN_CODESYS_AGENT_SETTINGS_EVENT, openCodesysAgentSettings);
    };
  }, []);

  useEffect(() => {
    if (showAnyAgentEntry) void refreshTiaAgentStatus(projectId);
  }, [projectId, settingsTick, showAnyAgentEntry, refreshTiaAgentStatus]);
  const selectedModelLabel = getModelDisplayLabel(selectedProvider, selectedModel, { isSuperAdmin });
  const routingModeLabel = routingMode === GenerationMode.THINKING ? t('Thinking') : t('Fast');
  const chipClassName = 'inline-flex h-8 max-w-full items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2.5 text-xs font-medium text-gray-600 dark:border-cyan-900/35 dark:bg-zinc-900/45 dark:text-zinc-300';

  const handleModelSelectionChange = useCallback((value: string) => {
    const [rawProvider, rawModel] = value.split('::');
    const newProvider = normalizeProvider(rawProvider);
    const newModel = rawModel || effectiveModels.find(m => m.provider === newProvider)?.value || effectiveModels[0].value;
    if (newProvider === 'custom') {
      const customSelection = getCustomModelConnections()
        .flatMap(conn => conn.models)
        .find(m => m.enabled !== false && m.modelId.trim() === newModel);
      if (customSelection) {
        saveCustomModelSelectionId(customSelection.id);
      }
    }
    setSelectedProvider(newProvider);
    setSelectedModel(newModel);
    setItem('ai_provider', newProvider);
    setItem('ai_model', newModel);
  }, [effectiveModels]);

  const modelSelectOptions = useMemo(() => effectiveProviders.map(provider => {
    const isCustom = provider.value === 'custom';
    const staticProviderModels = effectiveModels.filter(m => m.provider === provider.value);

    const customModelOptions: Array<{ id: string; modelId: string; label: string }> = isCustom
      ? getCustomModelConnections().flatMap(conn =>
          conn.models
            .filter(m => m.enabled !== false && m.modelId.trim())
            .map(m => ({
              id: m.id,
              modelId: m.modelId.trim(),
              label: m.displayName?.trim() || m.modelId.trim(),
            }))
        )
      : [];

    if (staticProviderModels.length === 0 && customModelOptions.length === 0) return null;
    // Offered but unrunnable was the worst of the three states: the turn failed
    // several stages in, on a provider nobody had a key for, and the error said
    // the keys were busy.
    const selectable = isProviderSelectable(provider.value, platformProviders);
    // Only a platform super admin receives rows hidden from users; they are
    // offered, marked, so a hidden model can still be tried before unhiding it.
    const hiddenSuffix = provider.visible === false ? ` — ${t('Hidden')}` : '';
    const keySuffix = selectable ? '' : ` — ${t('No key configured')}`;
    return (
      <optgroup
        key={provider.value}
        label={`${getProviderDisplayLabel(provider.value)}${hiddenSuffix}${keySuffix}`}
        disabled={!selectable}
        className="bg-white font-semibold text-cyan-700 dark:bg-[#161b22] dark:text-cyan-600">
        {!isCustom && staticProviderModels.map(m => (
          <option key={`${m.provider}::${m.value}`} value={`${m.provider}::${m.value}`} className="bg-white font-normal text-gray-700 dark:bg-[#161b22] dark:text-cyan-100">
            {m.label}{m.visible === false ? ` — ${t('Hidden')}` : ''}
          </option>
        ))}
        {isCustom && customModelOptions.map(m => (
          <option key={`custom::${m.id}`} value={`custom::${m.modelId}`} className="bg-white font-normal text-gray-700 dark:bg-[#161b22] dark:text-cyan-100">
            {m.label}
          </option>
        ))}
        {isCustom && customModelOptions.length === 0 && staticProviderModels.map(m => {
          const optionModel = resolveModelSelection(provider.value, m.value).model;
          return (
            <option key={`${m.provider}::${optionModel}`} value={`${m.provider}::${optionModel}`} className="bg-white font-normal text-gray-700 dark:bg-[#161b22] dark:text-cyan-100">
              {getModelDisplayLabel(m.provider, optionModel)}
            </option>
          );
        })}
      </optgroup>
    );
  }), [effectiveModels, effectiveProviders, platformProviders, t]);

  return (
    <div className="w-full max-w-[46rem] mx-auto px-4 pb-3">
      <div
        className={`relative bg-white dark:bg-[#161b22] rounded-2xl border flex flex-col transition-all focus-within:ring-1 focus-within:ring-cyan-500/50 focus-within:border-cyan-500/50 ${isDragOver
          ? 'border-cyan-400 ring-2 ring-cyan-400/30 shadow-lg shadow-cyan-500/10'
          : 'border-gray-200 dark:border-cyan-900/40 shadow-sm dark:shadow-cyan-900/5'}`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onPaste={handlePaste}
      >

        <ContextUsageBar usage={contextUsage} isChinese={uiLanguage === 'Chinese'} />
        {contextFull && <ContextFullNotice isChinese={uiLanguage === 'Chinese'} onNewConversation={onNewConversation} />}
        {/* The banner that stood here said "请先完成上方必填的需求确认，再继续对话。"
            directly above a placeholder reading "请先完成上方需求确认…" — the same
            sentence twice, and the second one is already inside the box the
            reader is looking at. It also stopped being true once the composer
            accepts a written answer. */}
        <div className="py-2 px-4 relative group/textareawrapper">
          {pickerMode ? (
            <ComposerPicker
              mode={pickerMode}
              mentions={mentionMatches}
              commands={commandMatches}
              activeIndex={pickerIndex}
              onActiveIndexChange={setPickerIndex}
              onPickMention={applyMentionPick}
              onPickCommand={applyCommandPick}
            />
          ) : null}
          {/* The TIA project the next turn validates in (P2 T5), where the TIA
              Agent entry shows; the picker itself shows nothing without a project. */}
          {showTiaAgentEntry ? (
            <TiaProjectPicker
              projectId={projectId}
              workMode={tiaWorkMode}
              settingsPlcModel={plcModel}
              onProjectChosen={handleTiaProjectChosen}
            />
          ) : null}
          {/* What the conversation holds already. Each conversation's list starts closed. */}
          <ConversationFiles
            key={sessionId}
            files={conversationFiles.files}
            onRemove={conversationFiles.remove}
            removeFailed={conversationFiles.removeFailed}
          />
          {/* P2-2: Attachment preview strip above textarea (Claude Web-style) */}
          {uploadedFiles.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 mb-2">
              {uploadedFiles.map(file => {
                const fileKey = file.localId || file.fileId || '';
                return (
                  <span
                    key={fileKey}
                    className="inline-flex items-center gap-1 rounded-full border border-cyan-200/60 bg-cyan-50/70 px-2.5 py-1 text-[11px] font-medium text-cyan-700 dark:border-cyan-500/20 dark:bg-cyan-950/30 dark:text-cyan-200"
                  >
                    <span className="max-w-[120px] truncate">{file.fileName}</span>
                    <button
                      type="button"
                      onClick={() => {
                        if (file.fileId) {
                          parsePollControllersRef.current.get(file.fileId)?.abort();
                          parsePollControllersRef.current.delete(file.fileId);
                          void deleteUploadedFile(file.fileId).catch(() => {});
                        }
                        for (const key of [file.localId, file.fileId]) {
                          if (!key) continue;
                          const blobUrl = localPreviewUrlsRef.current.get(key);
                          if (blobUrl) {
                            URL.revokeObjectURL(blobUrl);
                            localPreviewUrlsRef.current.delete(key);
                          }
                        }
                        setUploadedFiles(current => current.filter(item => (item.localId || item.fileId) !== fileKey));
                      }}
                      className="ml-0.5 rounded-full p-0.5 opacity-60 hover:opacity-100 hover:bg-rose-100 dark:hover:bg-rose-900/30 transition-colors"
                      aria-label={t('Remove file')}
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </span>
                );
              })}
              {uploadingCount > 0 && (
                <span className="text-[11px] text-zinc-400 dark:text-zinc-500">
                  {uploadingCount} {t('uploading...')}
                </span>
              )}
            </div>
          )}
          {showRestoredDraftNotice && (
            <div role="status" className="mb-1 flex items-center justify-between gap-2 text-[11px] text-gray-500 dark:text-zinc-400">
              <span>{t('Restored your unsent draft')}</span>
              <button
                type="button"
                onClick={discardRestoredDraft}
                className="rounded px-1.5 py-0.5 text-cyan-700 transition-colors hover:bg-cyan-50 dark:text-cyan-300 dark:hover:bg-cyan-900/30"
              >
                {t('Clear')}
              </button>
            </div>
          )}
          {/* The scroll fade and the expand button belong to the textarea, not
              to the top of the box: the TIA project picker, attachments and the
              draft notice can stand above it. */}
          <div className="relative">
            {isScrollable && (
              <>
                <div className="absolute top-0 -left-1 -right-1 h-6 pointer-events-none z-10 bg-gradient-to-b from-white/90 to-transparent dark:hidden" />
                <div className="absolute top-0 -left-1 -right-1 h-6 pointer-events-none z-10 bg-gradient-to-b from-[#161b22]/90 to-transparent hidden dark:block" />
              </>
            )}
            <textarea
              ref={textareaRef}
              value={prompt}
              onChange={(e) => {
                setPrompt(e.target.value);
                setCaret(e.target.selectionStart ?? e.target.value.length);
                setPickerDismissed(false);
                setShowSettings(false);
                setShowModelMenu(false);
              }}
              onSelect={(e) => setCaret((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
              onKeyDown={handleKeyDown}
              placeholder={hasActiveClarify ? t('Answer the confirmation above, or write your reply here...') : t('Describe your automation requirement...')}
              className="w-full min-h-[44px] bg-transparent border-none outline-none resize-none text-gray-900 dark:text-zinc-200 placeholder-gray-400 dark:placeholder-zinc-500 text-sm leading-relaxed custom-scrollbar"
              rows={1}
              maxLength={maxChars}
              disabled={contextFull}
            />
            {/* Expand button — visible when scrollable and not dragging */}
            {isScrollable && !isDragOver && (
              <button
                type="button"
                onClick={() => setIsExpandOpen(true)}
                className="absolute right-1 top-0 z-20 flex h-11 w-11 items-center justify-center rounded-md text-gray-400 hover:text-gray-600 dark:text-zinc-500 dark:hover:text-zinc-300 hover:bg-gray-100 dark:hover:bg-zinc-800 opacity-100 sm:opacity-0 sm:group-hover/textareawrapper:opacity-100 transition-opacity"
                title={t('Expand editor')}
                aria-label={t('Expand editor')}
              >
                  <Maximize2 size={16} />
              </button>
            )}
          </div>
          {maxChars !== undefined && prompt.length > maxChars * 0.8 && (
            <div className="mt-1 text-right text-[11px] text-gray-400 dark:text-zinc-500">
              <span className={prompt.length >= maxChars ? 'text-red-500' : undefined}>
                {prompt.length.toLocaleString()}
              </span>
              {' / '}
              {maxChars.toLocaleString()}
            </div>
          )}
        </div>

        {/* Expanded Settings Panel */}
        {showSettings && (
          <div ref={settingsRef} className="mx-3 grid grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 dark:border-cyan-900/40 dark:bg-[#0d1117] md:grid-cols-2">
            <div className="flex items-start justify-between gap-4 md:col-span-2">
              <p className="text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">{t('Saved as the default engineering context for future conversations.')}</p>
              <button type="button" onClick={resetEngineeringContext} className="min-h-9 shrink-0 rounded-md px-3 text-xs font-medium text-cyan-700 hover:bg-cyan-50 dark:text-cyan-300 dark:hover:bg-cyan-900/30">
                {t('Reset defaults')}
              </button>
            </div>
            <div className="space-y-1">
              <label htmlFor="engineering-target-platform" className="text-[10px] font-semibold text-cyan-600 dark:text-cyan-500 uppercase tracking-wider">{t('Target Platform')}</label>
              {/* Locked while a turn is running.
                  A turn plans against one platform and generates against
                  another if this changes underneath it — the 2026-09-07
                  walkthrough produced exactly that mix, a CODESYS plan followed
                  by a Siemens generation, after the platform was changed
                  mid-run. The change is kept for the next turn instead. */}
              <select
                id="engineering-target-platform"
                value={brand}
                disabled={isLoading}
                title={isLoading ? t('The target platform is fixed while a turn is running.') : undefined}
                onChange={(e) => {
                  const nextBrand = e.target.value as PlcBrand;
                  setBrand(nextBrand);
                  savePlcBrand(nextBrand);
                  updateTargetProfile({ brand: nextBrand });
                }}
                className="w-full bg-white dark:bg-[#161b22] border border-gray-200 dark:border-cyan-900/50 text-gray-700 dark:text-cyan-100 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-cyan-500 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
              >
                {PLC_BRANDS.map((b) => (
                  <option key={b.value} value={b.value}>{b.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label htmlFor="engineering-programming-language" className="text-[10px] font-semibold text-cyan-600 dark:text-cyan-500 uppercase tracking-wider">{t('Programming Language')}</label>
              {/* Locked while a turn is running, for the same reason as the
                  platform above; only the platform's own languages are offered. */}
              <select
                id="engineering-programming-language"
                value={effectiveLanguage}
                disabled={isLoading}
                title={isLoading ? t('The programming language is fixed while a turn is running.') : undefined}
                onChange={(e) => {
                  const nextLanguage = e.target.value as PlcLanguage;
                  setLanguage(nextLanguage);
                  savePlcLanguage(nextLanguage);
                }}
                className="w-full bg-white dark:bg-[#161b22] border border-gray-200 dark:border-cyan-900/50 text-gray-700 dark:text-cyan-100 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-cyan-500 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
              >
                {languagesForBrand(brand).map((l) => (
                  <option key={l.value} value={l.value}>{t(l.label)}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label htmlFor="engineering-software-version" className="text-[10px] font-semibold text-cyan-600 dark:text-cyan-500 uppercase tracking-wider">{t('Software Version')} <span className="text-zinc-400 font-normal">({t('Optional')})</span></label>
              <input
                id="engineering-software-version"
                type="text"
                value={softwareVersion}
                onChange={(e) => {
                  const value = e.target.value;
                  setSoftwareVersion(value);
                  setItem('software_version', value);
                  updateTargetProfile({ softwareVersion: trimOptional(value) });
                }}
                placeholder={t('e.g. TIA Portal v18')}
                className="w-full bg-white dark:bg-[#161b22] border border-gray-200 dark:border-cyan-900/50 text-gray-700 dark:text-cyan-100 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-cyan-500 transition-colors placeholder:text-gray-400 dark:placeholder:text-zinc-500"
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="engineering-hardware-series" className="text-[10px] font-semibold text-cyan-600 dark:text-cyan-500 uppercase tracking-wider">{t('Hardware Series')} <span className="text-zinc-400 font-normal">({t('Optional')})</span></label>
              <input
                id="engineering-hardware-series"
                type="text"
                value={hardwareSeries}
                onChange={(e) => {
                  const value = e.target.value;
                  setHardwareSeries(value);
                  updateTargetProfile({ hardwareSeries: trimOptional(value) });
                }}
                placeholder={t('e.g. S7-1200')}
                className="w-full bg-white dark:bg-[#161b22] border border-gray-200 dark:border-cyan-900/50 text-gray-700 dark:text-cyan-100 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-cyan-500 transition-colors placeholder:text-gray-400 dark:placeholder:text-zinc-500"
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="engineering-plc-model" className="text-[10px] font-semibold text-cyan-600 dark:text-cyan-500 uppercase tracking-wider">{t('PLC Model')} <span className="text-zinc-400 font-normal">({t('Optional')})</span></label>
              <input
                id="engineering-plc-model"
                type="text"
                value={plcModel}
                onChange={(e) => {
                  const value = e.target.value;
                  setPlcModel(value);
                  setItem('plc_model', value);
                  updateTargetProfile({ plcModel: trimOptional(value) });
                }}
                placeholder={t('e.g. S7-1215C DC/DC/DC')}
                className="w-full bg-white dark:bg-[#161b22] border border-gray-200 dark:border-cyan-900/50 text-gray-700 dark:text-cyan-100 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-cyan-500 transition-colors placeholder:text-gray-400 dark:placeholder:text-zinc-500"
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="engineering-firmware-version" className="text-[10px] font-semibold text-cyan-600 dark:text-cyan-500 uppercase tracking-wider">{t('Firmware Version')} <span className="text-zinc-400 font-normal">({t('Optional')})</span></label>
              <input
                id="engineering-firmware-version"
                type="text"
                value={firmwareVersion}
                onChange={(e) => {
                  const value = e.target.value;
                  setFirmwareVersion(value);
                  updateTargetProfile({ firmwareVersion: trimOptional(value) });
                }}
                placeholder={t('e.g. V4.5')}
                className="w-full bg-white dark:bg-[#161b22] border border-gray-200 dark:border-cyan-900/50 text-gray-700 dark:text-cyan-100 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-cyan-500 transition-colors placeholder:text-gray-400 dark:placeholder:text-zinc-500"
              />
            </div>
            {/* TIA only: where the Agent validates and creates projects (P2 E12). */}
            {brand === PlcBrand.Siemens ? (
              <div className="space-y-1 md:col-span-2">
                <span id="engineering-tia-work-mode" className="text-[10px] font-semibold text-cyan-600 dark:text-cyan-500 uppercase tracking-wider">{sayTia(TIA_WORK_MODE_TEXT.label)}</span>
                <div role="group" aria-labelledby="engineering-tia-work-mode" className="flex gap-1 rounded-lg border border-gray-200 bg-white p-1 dark:border-cyan-900/50 dark:bg-[#161b22]">
                  {(['background', 'window'] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      aria-pressed={tiaWorkMode === mode}
                      onClick={() => chooseTiaWorkMode(mode)}
                      className={`flex-1 rounded-md px-2 py-1 text-xs transition-colors ${tiaWorkMode === mode
                        ? 'bg-cyan-50 font-semibold text-cyan-700 shadow-sm dark:bg-cyan-900/40 dark:text-cyan-300'
                        : 'text-gray-500 hover:text-gray-700 dark:text-zinc-400 dark:hover:text-zinc-200'}`}
                    >
                      {sayTia(TIA_WORK_MODE_TEXT.options[mode])}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] leading-relaxed text-zinc-500 dark:text-zinc-400">{sayTia(TIA_WORK_MODE_TEXT.explanation)}</p>
              </div>
            ) : null}
          </div>
        )}

        {(uploadedFiles.length > 0 || uploadingCount > 0 || uploadError) && (
          <div className="px-5 pb-2">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-semibold text-gray-400 dark:text-zinc-500 uppercase tracking-wider">
                {t('Files')}: {uploadedFiles.length}/{Math.max(0, MAX_CHAT_FILES - heldFileCount)}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {uploadedFiles.map(file => {
                const isImage = /^image\//.test(file.mimeType);
                const fileKey = file.localId || file.fileId || '';
                const thumbnailUrl = isImage
                  ? localPreviewUrlsRef.current.get(file.fileId || file.localId || '')
                  : undefined;

                // Derive border/background color from cardStatus
                const statusStyles: Record<string, string> = {
                  uploading: 'border-cyan-400 dark:border-cyan-500 bg-cyan-50 dark:bg-cyan-950/30 text-cyan-700 dark:text-cyan-300',
                  scanning: 'border-cyan-400 dark:border-cyan-500 bg-cyan-50 dark:bg-cyan-950/30 text-cyan-700 dark:text-cyan-300',
                  converting: 'border-amber-400 dark:border-amber-600/50 bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-300',
                  processing: 'border-cyan-400 dark:border-cyan-500 bg-cyan-50 dark:bg-cyan-950/30 text-cyan-700 dark:text-cyan-300',
                  ready: 'border-green-400 dark:border-green-600/50 bg-green-50 dark:bg-green-950/20 text-green-700 dark:text-green-300',
                  warning: 'border-amber-400 dark:border-amber-600/50 bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-300',
                  failed: 'border-red-400 dark:border-red-600/50 bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-300',
                };
                const cardStyle = statusStyles[file.cardStatus] || statusStyles.uploading;

                // Status label text and color
                let statusText = '';
                let statusColor = 'text-cyan-600 dark:text-cyan-400';
                switch (file.cardStatus) {
                  case 'uploading':
                    statusText = t('Uploading & parsing...');
                    statusColor = 'text-cyan-600 dark:text-cyan-400';
                    break;
                  case 'scanning':
                    statusText = t('Scanning...');
                    statusColor = 'text-cyan-600 dark:text-cyan-400';
                    break;
                  case 'converting':
                    statusText = t('Converting...');
                    statusColor = 'text-amber-600 dark:text-amber-400';
                    break;
                  case 'processing':
                    if (file.contextStatus === 'pending' || file.contextStatus === 'processing') {
                      statusText = isImage ? t('Understanding image...') : t('Preparing context...');
                    } else {
                      statusText = t('Parsing...');
                    }
                    statusColor = 'text-cyan-600 dark:text-cyan-400';
                    break;
                  // Once read: ready, or the server's sentence on what was not
                  // read, translated here. The same words as in the list of
                  // the conversation's files (uploadedFileReadResult).
                  case 'ready':
                    statusText = t(uploadedFileReadResult(file) || '');
                    statusColor = 'text-green-600 dark:text-green-400';
                    break;
                  case 'warning':
                    statusText = t(uploadedFileReadResult(file) || '');
                    statusColor = 'text-amber-600 dark:text-amber-400';
                    break;
                  case 'failed':
                    statusText = t(uploadedFileReadResult(file) || '');
                    statusColor = 'text-red-600 dark:text-red-400';
                    break;
                }

                return (
                  <div
                    key={fileKey}
                    className={`relative flex max-w-full items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs overflow-hidden ${cardStyle}`}
                  >
                    {/* Image thumbnail — local blob during upload, server URL after */}
                    {isImage && thumbnailUrl ? (
                      <div className="relative shrink-0">
                        <img
                          src={thumbnailUrl}
                          alt={file.fileName}
                          className={`w-8 h-8 rounded object-cover cursor-pointer ${file.cardStatus === 'uploading' ? 'opacity-50' : ''}`}
                          onClick={() => (file.cardStatus === 'ready' || file.cardStatus === 'warning') && void openImagePreview(file)}
                        />
                        {file.cardStatus === 'uploading' && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Loader2 size={12} className="animate-spin text-white drop-shadow-lg" />
                          </div>
                        )}
                      </div>
                    ) : (
                      getFileTypeIcon(file.fileName)
                    )}
                    <div className="flex flex-col min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="max-w-[140px] truncate font-medium">{file.fileName}</span>
                        <span className="shrink-0 opacity-60">{formatFileSize(file.sizeBytes)}</span>
                      </div>
                      {/* Whole: on a failed read this is the sentence that says what to do instead. */}
                      <span className={`break-words ${statusColor}`} title={statusText}>
                        {(file.cardStatus === 'uploading' || file.cardStatus === 'scanning' || file.cardStatus === 'converting' || file.cardStatus === 'processing') && (
                          <Loader2 size={11} className="inline animate-spin mr-1 -mt-0.5" />
                        )}
                        {file.cardStatus === 'ready' && <>&#10003; </>}
                        {statusText}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (file.fileId) {
                          parsePollControllersRef.current.get(file.fileId)?.abort();
                          parsePollControllersRef.current.delete(file.fileId);
                          void deleteUploadedFile(file.fileId).catch((error) => {
                            setUploadError(error instanceof Error ? error.message : t('Failed to delete uploaded file'));
                          });
                        }
                        // Revoke local blob URL (check both placeholder key and server fileId)
                        for (const key of [file.localId, file.fileId]) {
                          if (!key) continue;
                          const blobUrl = localPreviewUrlsRef.current.get(key);
                          if (blobUrl) {
                            URL.revokeObjectURL(blobUrl);
                            localPreviewUrlsRef.current.delete(key);
                          }
                        }
                        setUploadedFiles(current => current.filter(item => (item.localId || item.fileId) !== fileKey));
                      }}
                      className="ml-auto shrink-0 rounded-full p-0.5 opacity-50 hover:opacity-100 transition-opacity hover:bg-black/10 dark:hover:bg-white/10"
                      aria-label={t('Remove file')}
                    >
                      <X size={12} />
                    </button>
                    {/* Progress bar for uploading files */}
                    {file.cardStatus === 'uploading' && file.uploadProgress !== undefined && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-300 dark:bg-zinc-700">
                        <div
                          className="h-full bg-cyan-500 transition-all duration-300 rounded-full"
                          style={{ width: `${Math.round(file.uploadProgress * 100)}%` }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
              {uploadError && (
                <div className="flex min-w-0 items-start gap-2 rounded-lg border border-red-200 dark:border-red-900/30 bg-red-50 dark:bg-red-950/20 px-2.5 py-1.5 text-xs text-red-600 dark:text-red-400">
                  <AlertCircle size={13} className="mt-px shrink-0" />
                  {/* Whole, on as many lines as it takes: what to do about a refused file is the end of its sentence. */}
                  <span className="min-w-0 whitespace-pre-line break-words">{uploadError}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Drag & Drop Overlay */}
        {isDragOver && (
          <div className="absolute inset-0 z-20 rounded-2xl flex items-center justify-center bg-cyan-500/10 dark:bg-cyan-500/15 border-2 border-cyan-400 border-dashed backdrop-blur-sm pointer-events-none animate-in fade-in duration-150">
            <div className="flex flex-col items-center gap-2">
              <Paperclip size={32} className="text-cyan-500 animate-bounce" />
              <span className="text-sm font-semibold text-cyan-600 dark:text-cyan-400">{t('Drop files to upload')}</span>
            </div>
          </div>
        )}

        {/* Bottom Toolbar */}
        <div className="relative flex items-center justify-between gap-3 px-5 pb-5 pt-2">
          {/* Left Actions: Plus Button & Model Selector Pill */}
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="relative" ref={plusMenuRef}>
              <button
                type="button"
                onClick={() => setShowPlusMenu(!showPlusMenu)}
                aria-label={t('Add files or configure tools')}
                aria-expanded={showPlusMenu}
                aria-haspopup="menu"
                className={`w-11 h-11 rounded-full border flex items-center justify-center transition-all shrink-0 relative ${showPlusMenu
                  ? 'border-cyan-500 text-cyan-600 bg-cyan-100/50 dark:text-cyan-200 dark:bg-cyan-900/30'
                  : 'border-cyan-200 dark:border-cyan-900/50 text-cyan-600 dark:text-cyan-500 hover:text-cyan-700 dark:hover:text-cyan-300 hover:bg-cyan-50 dark:hover:bg-cyan-900/20'
                  }`}
              >
                <Plus size={16} className={`transition-transform duration-200 ${showPlusMenu ? 'rotate-45' : ''}`} />
                
                {/* Status Indicator for active modifiers (SSM / Search) */}
                {(strictSafetyCheck || useWebSearch) && !showPlusMenu && (
                  <span className="absolute -right-1 -top-1 flex gap-0.5" aria-hidden="true">
                    {strictSafetyCheck && <span className="h-2.5 w-2.5 rounded-full border-2 border-white bg-emerald-500 dark:border-[#161b22]" />}
                    {useWebSearch && <span className="h-2.5 w-2.5 rounded-full border-2 border-white bg-cyan-500 dark:border-[#161b22]" />}
                  </span>
                )}
              </button>

              {/* Plus Menu Dropdown */}
              {showPlusMenu && (
                <div role="menu" className="absolute bottom-full left-0 mb-2 w-56 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-cyan-900/50 rounded-xl shadow-lg dark:shadow-cyan-900/20 overflow-hidden z-50 animate-in fade-in slide-in-from-bottom-2 duration-200">
                  <div className="p-1.5 flex flex-col">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        fileInputRef.current?.click();
                        setShowPlusMenu(false);
                      }}
                      className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-cyan-100 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-cyan-900/40 rounded-lg transition-colors w-full text-left"
                    >
                      <span className="relative">
                        <Paperclip size={16} className="text-gray-400" />
                        {uploadedFiles.length > 0 && (
                          <span className="absolute -top-1.5 -right-2 w-4 h-4 rounded-full bg-cyan-500 text-white text-[9px] font-bold flex items-center justify-center leading-none">
                            {uploadedFiles.length}
                          </span>
                        )}
                      </span>
                      <span className="flex-1">{t('Upload File')}</span>
                    </button>
                    <div className="my-1 border-t border-gray-100 dark:border-zinc-800" />
                    <button
                      type="button"
                      role="menuitemcheckbox"
                      aria-checked={useWebSearch}
                      onClick={() => setUseWebSearch(current => !current)}
                      className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-cyan-100 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-cyan-900/40 rounded-lg transition-colors w-full text-left"
                    >
                      <Globe size={16} className={useWebSearch ? 'text-cyan-500' : 'text-gray-400'} />
                      <span className="flex-1">{t('Search Web')}</span>
                      {useWebSearch && <span className="w-2 h-2 rounded-full bg-cyan-500 flex-shrink-0" />}
                    </button>
                    <button
                      type="button"
                      role="menuitemcheckbox"
                      aria-checked={strictSafetyCheck}
                      onClick={() => setStrictSafetyCheck(current => !current)}
                      className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-cyan-100 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-cyan-900/40 rounded-lg transition-colors w-full text-left"
                    >
                      <ShieldCheck size={16} className={strictSafetyCheck ? 'text-emerald-500' : 'text-gray-400'} />
                      <span className="flex-1">{t('Strict Safety Check')}</span>
                      {strictSafetyCheck && <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />}
                    </button>
                  </div>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ALLOWED_FILE_ACCEPT}
                className="hidden"
                onChange={(event) => {
                  // Read, then emptied. A browser reports a selection only when
                  // it differs from what the input holds: a refused file made
                  // smaller under its own name and chosen again was no news.
                  const chosen = Array.from(event.target.files ?? []);
                  event.target.value = '';
                  void handleFilesSelected(chosen);
                }}
              />
            </div>

            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setShowSettings(!showSettings)}
                className={`${chipClassName} cursor-pointer hover:border-cyan-300 hover:bg-cyan-50 dark:hover:border-cyan-700/50 dark:hover:bg-cyan-900/25 transition-colors ${showSettings ? 'border-cyan-300 bg-cyan-50 text-cyan-700 dark:border-cyan-500/40 dark:bg-cyan-900/30 dark:text-cyan-200' : ''}`}
                title={t('Configure engineering context')}
                aria-expanded={showSettings}
              >
                <Cpu size={13} className="shrink-0 text-cyan-600 dark:text-cyan-400" />
                <span className="truncate">{selectedBrandLabel}</span>
                <span className="text-gray-300 dark:text-zinc-600" aria-hidden="true">&middot;</span>
                <span className="truncate">{selectedLanguageLabel}</span>
              </button>
              <div className="relative" ref={modelMenuRef}>
                <button
                  type="button"
                  onClick={() => setShowModelMenu(!showModelMenu)}
                  className={`${chipClassName} cursor-pointer hover:border-cyan-300 hover:bg-cyan-50 dark:hover:border-cyan-700/50 dark:hover:bg-cyan-900/25 transition-colors ${showModelMenu ? 'border-cyan-300 bg-cyan-50 text-cyan-700 dark:border-cyan-500/40 dark:bg-cyan-900/30 dark:text-cyan-200' : ''}`}
                  title={`${getProviderDisplayLabel(selectedProvider)} / ${selectedModelLabel}`}
                  aria-expanded={showModelMenu}
                  aria-haspopup="dialog"
                >
                  <Brain size={13} className="shrink-0 text-purple-600 dark:text-purple-400" />
                  <span className="truncate">{selectedModelLabel}</span>
                  <span className="text-gray-300 dark:text-zinc-600" aria-hidden="true">&middot;</span>
                  <span className="truncate">{routingModeLabel}</span>
                </button>
                {showModelMenu && (
                  <div role="dialog" aria-label={t('Model and Routing')} className="absolute bottom-full left-0 mb-2 w-64 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-cyan-900/50 rounded-xl shadow-lg dark:shadow-cyan-900/20 overflow-hidden z-50 animate-in fade-in slide-in-from-bottom-2 duration-200">
                    <div className="p-3 space-y-3">
                      <div className="space-y-1">
                        <label htmlFor="composer-model-select" className="text-[10px] font-semibold text-cyan-600 dark:text-cyan-500 uppercase tracking-wider">{t('Model and Routing')}</label>
                        <select
                          id="composer-model-select"
                          value={`${selectedProvider}::${selectedModel}`}
                          onChange={(e) => {
                            handleModelSelectionChange(e.target.value);
                            setShowModelMenu(false);
                          }}
                          className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700 transition-colors focus:border-cyan-500 focus:outline-none dark:border-cyan-900/50 dark:bg-[#0d1117] dark:text-cyan-100"
                        >
                          {modelSelectOptions}
                        </select>
                      </div>
                      <div className="flex rounded-lg border border-gray-200 bg-white p-1 dark:border-cyan-900/50 dark:bg-[#0d1117]">
                        <button
                          type="button"
                          aria-pressed={routingMode === GenerationMode.FAST}
                          onClick={() => { setRoutingMode(GenerationMode.FAST); setItem('routing_mode', GenerationMode.FAST); }}
                          className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-bold transition-all flex-1 justify-center ${routingMode === GenerationMode.FAST
                            ? 'bg-cyan-50 text-cyan-700 shadow-sm dark:bg-cyan-900/40 dark:text-cyan-300'
                            : 'text-gray-400 hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300'}`}
                          title={fastModeTitle}
                        >
                          <Zap size={12} className={routingMode === GenerationMode.FAST ? 'fill-current' : ''} />
                          {t('Fast')}
                        </button>
                        <button
                          type="button"
                          aria-pressed={routingMode === GenerationMode.THINKING}
                          onClick={() => { setRoutingMode(GenerationMode.THINKING); setItem('routing_mode', GenerationMode.THINKING); }}
                          className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-bold transition-all flex-1 justify-center ${routingMode === GenerationMode.THINKING
                            ? 'bg-purple-50 text-purple-700 shadow-sm dark:bg-purple-900/40 dark:text-purple-300'
                            : 'text-gray-400 hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300'}`}
                          title={thinkingModeTitle}
                        >
                          <Brain size={12} className={routingMode === GenerationMode.THINKING ? 'fill-current' : ''} />
                          {t('Thinking')}
                        </button>
                      </div>
                      <p className="text-[11px] leading-relaxed text-zinc-500 dark:text-zinc-400">
                        {routingMode === GenerationMode.THINKING ? thinkingModeTitle : fastModeTitle}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              {showTiaAgentEntry ? (
                <button
                  type="button"
                  onClick={() => {
                    setShowSettings(false);
                    setShowModelMenu(false);
                    setShowTiaAgentSettings(true);
                  }}
                  className={`inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border px-2 text-xs font-semibold transition-colors ${tiaAgentEntryTone}`}
                  title={tiaAgentButtonLabel}
                  aria-label="TIA Agent"
                >
                  {tiaAgentEntryIcon}
                  <span>TIA Agent</span>
                  <span className="font-normal opacity-80">{tiaAgentButtonText}</span>
                </button>
              ) : null}
              {showCodesysAgentEntry ? (
                <button
                  type="button"
                  onClick={() => {
                    setShowSettings(false);
                    setShowModelMenu(false);
                    setShowCodesysAgentSettings(true);
                  }}
                  className={`inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border px-2 text-xs font-semibold transition-colors ${codesysReady
                    ? 'border-emerald-200 bg-emerald-50/70 text-emerald-800 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300'
                    : connectionState === 'online'
                      ? 'border-amber-200 bg-amber-50/70 text-amber-800 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300'
                      : tiaAgentLoadState === 'detecting' || tiaAgentLoadState === 'stale'
                        ? 'border-zinc-200 bg-zinc-50 text-zinc-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300'
                        : 'border-red-200 bg-red-50/70 text-red-700 hover:bg-red-100 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300'}`}
                  title={codesysReady
                    ? t('CODESYS Agent Online · Ready')
                    : connectionState === 'online'
                      ? t('CODESYS Agent Online · setup required')
                      : t('CODESYS Agent Offline')}
                  aria-label="CODESYS Agent"
                >
                  {codesysAgentEntryIcon}
                  <span>CODESYS Agent</span>
                  <span className="font-normal opacity-80">
                    {codesysReady ? t('Ready') : connectionState === 'online' ? t('Setup') : tiaAgentLoadState === 'detecting' ? t('Detecting') : t('Offline')}
                  </span>
                </button>
              ) : null}
              {/* V1 P0: Manual TIA action buttons (visible when agent is online) — Snapshot removed per user request */}
            </div>

          </div>

          {/* Right Actions: Send/Stop */}
          <div className="flex shrink-0 items-center gap-2">

            <div className="flex items-center gap-3">
              {isLoading && onStop ? (
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); onStop(); }}
                  className="w-11 h-11 rounded-full flex items-center justify-center transition-all shrink-0 bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 hover:text-red-600 dark:hover:text-red-300 shadow-sm border border-red-100 dark:border-red-900/30"
                  title={t('End Conversation')}
                  aria-label={t('Stop generation')}
                >
                  <Square size={14} className="fill-current" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={sendDisabled}
                  aria-label={t('Send message')}
                  title={t('Send message')}
                  className={`w-11 h-11 rounded-full flex items-center justify-center transition-all shrink-0 ${sendDisabled
                    ? 'bg-gray-100 dark:bg-cyan-900/20 text-gray-300 dark:text-cyan-800 cursor-not-allowed'
                    : 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-400 hover:to-blue-400 shadow-md'
                    }`}
                >
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <ArrowUp size={18} strokeWidth={2.5} />
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Expand Input Modal */}
      {isExpandOpen && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('Expand editor')}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsExpandOpen(false);
              textareaRef.current?.focus();
            }
          }}
        >
          <div className="w-full max-w-3xl mx-4 bg-white dark:bg-[#161b22] rounded-2xl border border-gray-200 dark:border-cyan-900/40 shadow-2xl flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-zinc-800">
              <span className="text-xs text-gray-400 dark:text-zinc-500">
                {t('Press Ctrl or Command + Enter to send. Enter inserts a new line. Esc closes the editor.')}
              </span>
              <button
                type="button"
                onClick={() => { setIsExpandOpen(false); textareaRef.current?.focus(); }}
                className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:text-zinc-500 dark:hover:text-zinc-300 hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors"
                aria-label={t('Close')}
              >
                <X size={16} />
              </button>
            </div>
            {/* Textarea */}
            <div className="p-5">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleExpandKeyDown}
                placeholder={t('Describe your automation requirement...')}
                className="w-full min-h-[60vh] max-h-[80vh] bg-transparent border-none outline-none resize-none text-gray-900 dark:text-zinc-200 placeholder-gray-400 dark:placeholder-zinc-500 text-sm leading-relaxed font-mono custom-scrollbar"
                autoFocus
                onFocus={(e) => {
                  const len = e.target.value.length;
                  e.target.setSelectionRange(len, len);
                }}
              />
            </div>
            <div className="flex items-center justify-end border-t border-gray-100 px-5 py-3 dark:border-zinc-800">
              <button
                type="button"
                onClick={() => {
                  if (sendDisabled) return;
                  handleSubmit();
                  setIsExpandOpen(false);
                  textareaRef.current?.focus();
                }}
                disabled={sendDisabled}
                className={`min-h-11 rounded-lg px-5 text-sm font-medium transition-colors ${sendDisabled
                  ? 'cursor-not-allowed bg-zinc-100 text-zinc-400 dark:bg-zinc-800 dark:text-zinc-600'
                  : 'bg-cyan-600 text-white hover:bg-cyan-500'}`}
              >
                {t('Send message')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <div className="text-center mt-3">
        <p className="text-[11px] text-gray-400 dark:text-zinc-500">
          {t('AI can make mistakes. Please verify generated logic before deploying to production hardware.')}
        </p>
      </div>

      {/* Image preview lightbox */}
      {previewImage && (
        <ImagePreview
          imageUrl={previewImage.imageUrl}
          fileName={previewImage.fileName}
          onClose={closeImagePreview}
        />
      )}
      <TiaAgentSettingsDialog
        isOpen={showTiaAgentSettings}
        projectId={projectId}
        onClose={() => setShowTiaAgentSettings(false)}
        onStatusChange={() => {}}
      />
      <CodesysAgentSettingsDialog
        isOpen={showCodesysAgentSettings}
        projectId={projectId}
        onClose={() => setShowCodesysAgentSettings(false)}
      />
    </div>
  );
};

export const ChatInput = memo(ChatInputComponent);
